import { FC, ReactElement, ReactNode, useState, useId } from 'react';

import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { useMedia } from '@pp/core/src/hooks/useMedia';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { GameStore as store, useGameDispatch } from '../../store/GameStore';
import { useHighflyerGameStateSlice } from '../../store/slices/highflyerGame/selectors';
import { inGameHistoryActions } from '../../store/slices/inGameHistory/slice';
import { recentResultDetailStateActions } from '../../store/slices/recentResultDetail/slice';

import styles from './TableTabs.module.css';

export interface TabProps {
  label: ReactElement;
  content: ReactElement | ReactNode;
}

export interface TabsProps {
  tabs: TabProps[];
  initActiveTab?: number;
  gameStyle?: boolean;
  className?: string;
  classNameContainer?: string;
  classNameLabel?: string;
  isOneWidth?: boolean;
}

export const TableTabs: FC<TabsProps> = ({
  tabs,
  gameStyle,
  className,
  classNameContainer,
  classNameLabel,
  initActiveTab = 0,
  isOneWidth,
}) => {
  const id = useId();
  const dispatch = useGameDispatch();
  const [activeTab, setActiveTab] = useState(initActiveTab);
  const mods = useModifiers(styles, 'tabs', { isOneWidth });
  const audioTrigger = useAudioTrigger();
  const isWide = useMedia('(min-width:1024px)');
  const isTabScrollbarAtTop = useHighflyerGameStateSlice.isTabScrollbarAtTop();
  const totalBet = useBetSlice.totalBet();
  const currentLanguage = useSettingsSlice.currentLanguage();
  const langMods = useModifiers(styles, 'tabs__header__item', {
    lang: currentLanguage === 'hy',
  });

  const heightMods = useModifiers(
    styles,
    'tabsHeight',
    {
      smallBetControls: isTabScrollbarAtTop && totalBet > 0,
      largeBetControls: totalBet > 0,
    },
    { withBaseClass: true }
  );

  const onChangeTab = (i: number): void => {
    setActiveTab(i);
    audioTrigger.play({ name: 'menuClick' });
    if (!isWide && i !== 3) {
      store.dispatch(recentResultDetailStateActions.setModalOpen(false));
    }
    dispatch(inGameHistoryActions.setApiRejected());
  };

  /* useEffect(() => {
    if (isWide) return;
    if (initActiveTab === 3 || isInlineResultModalOpened) {
      setActiveTab(3);
    }
  }, [initActiveTab, isInlineResultModalOpened]); */

  return (
    <div className={`${styles.tabs} ${gameStyle && styles.tabs_game} ${className} ${heightMods}`}>
      <div className={`${styles.tabs__header} ${mods} ${classNameLabel}`} data-testid="tabs-header">
        {tabs.map((tab, i) => (
          <div
            data-testid="tab"
            key={`specific-tab-${id}`}
            className={`${styles.tabs__header__item} ${activeTab === i ? styles.tabs__header__item_active : ''} ${langMods}`}
            onClick={() => onChangeTab(i)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <div className={`${styles.tabs__content} ${classNameContainer}`}>{tabs[activeTab].content}</div>
    </div>
  );
};
